/**
 * One field row — README §6: "field rows (label + hint left, control right,
 * max-width 720px)." Dispatches on `field.kind`; locking/inertness rules
 * come from task-18's gating table and "Four things that must be exactly
 * right" #1: personal fields (gate: 'personal') are always editable,
 * static-kind fields are always display-only (even for an admin — the
 * ops-layer itself refuses a static write "regardless of who is asking",
 * per task-3-report.md decision #3), the three open-decision ids
 * (sections.ts) always render "Undecided" rather than any real value, and
 * every other admin-gated tenant field renders inert with an amber `admin`
 * chip until the viewer has Admin.
 *
 * Task 33 (bug report: "the settings are all over the map instead of being
 * lined up properly"). The row used to be a `flex justify-between` with the
 * label side sized by `max-w-[320px] flex-none` — a max-width, not a fixed
 * width, so each row's label column shrank to its own label/hint length and
 * the control column started at a different x per row. A CSS grid with a
 * *definite* left-column track fixes that: every row that uses
 * `FIELD_ROW_GRID_CLASS` shares the exact same 260px label column and the
 * exact same 1fr control column, so controls of any kind (a 26px toggle, a
 * wide select, the whole hours block) all start at the same vertical edge.
 * The row itself now also carries the 720px cap directly (rather than only
 * the control wrapper doing so before), so a `FieldRow` is self-consistent
 * even outside SettingsPage's own `max-w-[720px]` section wrapper.
 *
 * Task 35 (Andrew: "they're too far left now instead of being right
 * justified in their rows"). Task 33 fixed the label column's width but left
 * the control column's own content flush against ITS left edge (the
 * fixed-width label column just moved that left edge rightward with it) —
 * not against the row's right edge, which is what README §6 "control right"
 * actually means: the row is label-left / control-right, with the gap
 * between them absorbing the slack, not a second fixed offset. The control
 * column div below now carries `justify-end`, so whatever it renders is
 * flush against the 720px row's right edge and the empty space lands
 * between the label and the control, where it belongs.
 *
 * That one `justify-end` is enough for every *single-element* control
 * (toggle, select, secret's Chip+Button, the plain text input) because each
 * is a lone flex child — it simply moves as a unit to the right edge. Two
 * kinds needed a second look because they are visually a group, not one
 * element, and "control right" has to mean something about the group's
 * own right edge, not just its container's:
 *   - number+unit: the `<input>` and its trailing unit span are one flex
 *     child group (`flex items-center gap-2`) inside the justify-end
 *     column, so the *group's* right edge — the unit label — is what lands
 *     on the row's right edge, not the bare input. The unit is being
 *     treated as part of the control (same convention as the `admin` chip
 *     riding along after a locked control), not a stray label off to the
 *     side, so this reads as one control hugging the edge rather than two
 *     things at two different edges.
 *   - text input specifically keeps `w-full max-w-[420px]`: every text field
 *     renders at the same 420px width regardless of its own value's length,
 *     so with `justify-end` all text-input right edges land on the exact
 *     same pixel — the "clean vertical line" the row exists to produce.
 * The two *block* controls (hours, logo) are handled where they're defined
 * (HoursField.tsx, LogoField.tsx) rather than here, because for those the
 * right choice is to right-justify the whole block as a unit — see the note
 * in HoursField.tsx for why.
 */
import { useEffect, useState } from 'react'
import type { ConfigField } from '@communicator/shared'
import { Chip, Select, Toggle } from '../ui'
import type { WeekHours } from '../../store'
import { OPEN_DECISION_FIELD_IDS } from './sections'
import { HoursField } from './HoursField'
import { LogoField } from './LogoField'
import { SecretField } from './SecretField'

/**
 * Shared by every field row so alignment is structural, not incidental:
 * `grid-cols-[260px_1fr]` gives the label column a fixed track (not a
 * max-width) so it can never shrink to content, and the row carries the
 * design's own 720px cap (README §6) directly.
 */
export const FIELD_ROW_GRID_CLASS =
  // STACKED ON A PHONE. The two-column form gives the label a fixed 260px,
  // which on a 390px screen leaves about a third of the width for the control
  // itself — a select you cannot read and a toggle you have to aim at. Below
  // the breakpoint the control drops under its label and gets the full width;
  // at md and up the desktop form is exactly as it was.
  //
  // Taller too: py-4 clears Apple's 44pt minimum for the row, so hitting the
  // one you meant does not require care.
  'grid grid-cols-1 gap-2 py-4 md:grid-cols-[260px_1fr] md:items-start md:gap-6 md:py-3 max-w-[720px] border-b border-subtle last:border-b-0'

export interface FieldRowProps {
  field: ConfigField
  value: unknown
  admin: boolean
  onCommit: (id: string, value: unknown) => Promise<void>
  error?: string | null
  /**
   * For a field whose real value lives in the deployment environment. Undefined
   * means either not env-backed, or an ops build too old to report it — in
   * which case nothing is claimed either way.
   */
  env?: { envVar: string; set: boolean; value?: string }
}

function toDisplayString(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

/**
 * zod's `.int()` on the ops-layer's argument schemas serializes to JSON
 * Schema `"integer"`, not `"number"` — task-18-brief.md notes this exact
 * bug shape has now bitten three other modules in this codebase. A settings
 * form has to coerce BOTH steps: the raw DOM value is always a string, and
 * even once parsed to a JS number it must still be forced to a real
 * integer — `Number("7.5")` happily returns `7.5`, and every `number`-kind
 * field here (minutes, days) is a whole count. Exported so the coercion
 * itself is unit-testable, not just observable through the rendered input.
 */
export function coerceInteger(raw: string): number | null {
  if (raw.trim() === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  return Math.trunc(n)
}

/**
 * The control column of a field row — README §6 "control right". `justify-end`
 * is the whole fix: it is the one shared class that makes every control kind
 * (a lone toggle, a lone select, an input+unit group, a locked control's
 * trailing `admin` chip) sit flush against the row's right edge instead of
 * flush against its own left edge, leaving the label-to-control gap to
 * absorb whatever slack a given control's own width doesn't use. Exported
 * (like `FIELD_ROW_GRID_CLASS`) so alignment is asserted on the exact class
 * string, not inferred from rendered pixel positions.
 */
export const CONTROL_COLUMN_CLASS =
  // Left-aligned under the label on a phone, right-aligned in the desktop's
  // second column — a control floated right under its own label reads as
  // belonging to the next row down.
  'flex min-w-0 items-center justify-start gap-2 md:justify-end'

export function FieldRow({ field, value, admin, onCommit, error, env }: FieldRowProps) {
  const isOpenDecision = OPEN_DECISION_FIELD_IDS.includes(field.id)
  // An env-backed field is locked for everyone, admin included. Editing it was
  // previously accepted and silently discarded — the row was written to the
  // config table and no service ever read it.
  const envBacked = field.envVar !== undefined
  const locked =
    envBacked || (field.kind !== 'static' && !isOpenDecision && field.gate === 'admin' && !admin)

  const [draft, setDraft] = useState(() => toDisplayString(value ?? field.default))
  useEffect(() => {
    setDraft(toDisplayString(value ?? field.default))
  }, [value, field.default])

  return (
    <div
      className={FIELD_ROW_GRID_CLASS}
      data-testid={`field-${field.id}`}
    >
      <div className="min-w-0">
        <p className="text-[15px] font-medium text-hi md:text-[13px]">{field.label}</p>
        <p className="text-[13px] leading-[1.45] text-lo md:text-[12px]">{field.hint}</p>
        {error ? <p className="mt-1 text-[12px] text-bad">{error}</p> : null}
      </div>
      <div className={CONTROL_COLUMN_CLASS}>
        {envBacked ? (
          // Display-only, and explicit about where the value actually comes
          // from. An empty box here used to mean both "nothing configured" and
          // "configured fine, somewhere this screen cannot see".
          <div className="flex flex-col gap-1" data-testid={`field-${field.id}-env`}>
            <div className="flex items-center gap-2">
              <Chip tone={env === undefined ? 'neutral' : env.set ? 'ok' : 'warn'}>
                {env === undefined ? 'Set in environment' : env.set ? 'Set' : 'Not set'}
              </Chip>
              <code className="text-[12px] text-mid">{field.envVar}</code>
            </div>
            {env?.value ? <span className="text-[13px] text-hi break-all">{env.value}</span> : null}
            <p className="text-[11px] text-lo">
              {field.kind === 'secret'
                ? 'Held in the deployment environment and never shown here. Change it there, then redeploy.'
                : 'Read from the deployment environment. Change it there, then redeploy.'}
            </p>
          </div>
        ) : isOpenDecision ? (
          <Chip tone="neutral">Undecided</Chip>
        ) : field.kind === 'static' ? (
          <span className="text-[13px] text-mid" data-testid={`field-${field.id}-value`}>
            {toDisplayString(value ?? field.default)}
          </span>
        ) : field.kind === 'toggle' ? (
          <Toggle
            checked={Boolean(value ?? field.default)}
            disabled={locked}
            aria-label={field.label}
            onChange={(checked) => void onCommit(field.id, checked)}
          />
        ) : field.kind === 'select' ? (
          <Select
            value={toDisplayString(value ?? field.default)}
            disabled={locked}
            aria-label={field.label}
            // The SAME `w-full max-w-[420px]` as the text input below, and for
            // the same reason. This was the one control in the whole settings
            // form with no width at all, so the browser sized it to whatever
            // value happened to be selected — which left "Appearance" showing
            // a box cut to fit "Tossker Auto", with nowhere for the longer
            // "Tossker Night" to go, and looking cramped beside the roomy
            // field directly beneath it. A select must be sized by its
            // WIDEST option, never its current one.
            className="w-full max-w-[420px]"
            options={(field.options ?? []).map((opt) => ({ value: opt, label: opt }))}
            onChange={(v) => void onCommit(field.id, v)}
          />
        ) : field.kind === 'number' ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={draft}
              disabled={locked}
              aria-label={field.label}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => {
                const n = coerceInteger(draft)
                if (n !== null) void onCommit(field.id, n)
              }}
              className="h-[34px] w-[90px] rounded-[8px] border border-subtle bg-card px-2.5 text-[13px] text-hi disabled:opacity-50"
            />
            {field.unit ? <span className="text-[12px] text-lo">{field.unit}</span> : null}
          </div>
        ) : field.kind === 'secret' ? (
          <SecretField field={field} locked={locked} onCommit={onCommit} />
        ) : field.kind === 'logo' ? (
          <LogoField field={field} locked={locked} />
        ) : field.kind === 'hours' ? (
          <HoursField field={field} value={value as WeekHours | undefined} locked={locked} onCommit={onCommit} />
        ) : (
          <input
            type="text"
            value={draft}
            disabled={locked}
            aria-label={field.label}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void onCommit(field.id, draft)}
            className="h-[34px] w-full max-w-[420px] rounded-[8px] border border-subtle bg-card px-2.5 text-[13px] text-hi disabled:opacity-50"
          />
        )}
        {locked ? <Chip tone="warn">admin</Chip> : null}
      </div>
    </div>
  )
}
